const IS_TEST_MODE = typeof jest !== 'undefined';

export enum Method {
	Saml = 'saml',
	Oidc = 'oidc',
	Xaa = 'xaa'
}

export enum SCIMMethod {
	Saml = 'saml',
	Oidc = 'oidc',
	Generic = 'generic'
}

export enum GuideType {
	SCIM = 'scim',
	SSO = 'sso',
	XAA = 'xaa'
}

export const GENERIC_IDP_ID = 'generic-idp';
export const GENERIC_DNP_ID = 'generic';

export const GROUP = 'group';
export const SUB = 'sub';
export const EXTERNAL_ID = 'externalID';
export const MASKED_VALUE = 'MASKED_VALUE';

export const CUSTOM_ATTRIBUTES_PREFIX = 'customAttributes.';

export enum IdPSamlInputFieldName {
	IdPSsoUrl = 'idp-sso-url',
	IdPIssuer = 'idp-issuer',
	IdPCertificate = 'idp-certificate',
	IdPMetadataUrl = 'idp-metadata-url'
}

export enum SPSamlDataFieldName {
	SpAcsUrl = 'sp-acs-url',
	SpEntityID = 'sp-entity-id',
	SpMetadataUrl = 'sp-metadata-url'
}

export enum SPOidcDataFieldName {
	CallbackUrl = 'callback-url'
}

export enum SPScimDataFieldName {
	SCIMToken = 'scim-bearer-token',
	SCIMURL = 'scim-url'
}

// Read-only values shown to the admin, to be copied into the IdP: the project-level ID-JAG
// audience the requesting app must present, and the tenant id the IdP must send in the
// assertion's aud_tenant claim (the audience names no tenant).
export enum SPXaaDataFieldName {
	Audience = 'xaa-audience',
	TenantId = 'xaa-tenant-id'
}

export type SPFieldType =
	| SPSamlDataFieldName
	| SPOidcDataFieldName
	| SPScimDataFieldName
	| SPXaaDataFieldName;

export enum IdPOidcInputFieldName {
	ProviderName = 'provider-name',
	ClientId = 'client-id',
	ClientSecret = 'client-secret',
	Scopes = 'scopes',
	GrantType = 'grant-type',
	Issuer = 'issuer',
	AuthorizationEndpoint = 'authorization-endpoint',
	TokenEndpoint = 'token-endpoint',
	UserInfoEndpoint = 'user-info-endpoint',
	JWKsEndpoint = 'jwks-endpoint',
	Prompt = 'prompt',
	UsePkce = 'use-pkce'
}

// XAA (Cross-App Access / ID-JAG) input fields. Every one is a content-placeable
// component: the content author positions each via <IdPInputField fieldName="..."> in
// the guide markdown (exactly like OIDC). The subject / login-id is NOT a field here -
// it is the externalID user-attribute mapping (<UserAttributes>), like OIDC.
export enum IdPXaaInputFieldName {
	Issuer = 'xaa-issuer',
	JwksUri = 'xaa-jwks-uri',
	SignAlgorithm = 'xaa-sign-algorithm',
	UserInfoUri = 'xaa-user-info-uri',
	Jit = 'xaa-jit'
}

export enum IdPCommonInputFieldName {
	SSODomains = 'sso-domains',
	UserAttributes = 'user-attributes',
	GroupsAttribute = 'groups-attribute-name',
	GroupsAttributeMapping = 'groups-attribute-mapping',
	DefaultSSORoles = 'default-sso-roles',
	RebacGroupsMappings = 'rebac-groups-mappings'
}

export type TIdPInputField =
	| IdPSamlInputFieldName
	| IdPOidcInputFieldName
	| IdPXaaInputFieldName
	| IdPCommonInputFieldName;

// mapping fields stored on the SSO configuration that also drive SCIM
// provisioning; editable from both SSO and SCIM guides
export const MAPPING_FIELD_NAMES: readonly IdPCommonInputFieldName[] = [
	IdPCommonInputFieldName.UserAttributes,
	IdPCommonInputFieldName.GroupsAttribute,
	IdPCommonInputFieldName.GroupsAttributeMapping,
	IdPCommonInputFieldName.DefaultSSORoles,
	IdPCommonInputFieldName.RebacGroupsMappings
];

export enum OidcPrompts {
	None = 'none',
	Login = 'login',
	Consent = 'consent',
	SelectAccount = 'select_account'
}

export enum OidcBaseScopes {
	OpenId = 'openid',
	Profile = 'profile',
	Email = 'email'
}

export enum OidcGrantTypes {
	AuthorizationCode = 'authorization_code',
	Implicit = 'implicit'
}

// Cross-App Access (XAA / ID-JAG) ID token signing algorithms. RS256 is the default.
export enum XaaSignAlgorithms {
	RS256 = 'RS256',
	ES256 = 'ES256',
	ES384 = 'ES384',
	ES512 = 'ES512'
}

// XAA JIT provisioning as a content-placeable yes/no select. 'enabled' maps to
// issuer.jitDisabled=false, 'disabled' to true.
export enum XaaJit {
	Enabled = 'enabled',
	Disabled = 'disabled'
}

export enum MdComponentNames {
	IdPInputField = 'IdPInputField',
	SPDataField = 'SPDataField',
	UserAttributes = 'UserAttributes',
	GroupsAttribute = 'GroupsAttribute',
	SPSCIMTokenField = 'SPSCIMTokenField',
	VerifyDomainInput = 'VerifyDomainInput',
	DnsRecordInfo = 'DnsRecordInfo'
}

export const MethodToIdPInputFieldNames = {
	[Method.Oidc]: IdPOidcInputFieldName,
	[Method.Saml]: IdPSamlInputFieldName,
	[Method.Xaa]: IdPXaaInputFieldName
};

export const SSO_TEST_CALLBACK_EVENT_TYPE = 'sso-test-callback';
export const SSO_TEST_CHANNEL = 'sso-test';

export { IS_TEST_MODE };
